import { IncorporationRequestDataType } from '@commenda/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { LegacyDocumentType, WorkRequestType } from '@prisma/client';
import { Country, State } from 'country-state-city';
import { DocumentSummaryField, SummaryFieldKS } from '../../common';
import { getDirectorName } from '../../Director/common';

export const UAEDirectorField = ({
  director,
  data,
  workRequestType,
}: {
  director: DirectorBase;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  workRequestType?: WorkRequestType;
}) => {
  const corporationHasDirector = director?.corporationHasDirector?.find(
    (dirWithCorp) =>
      dirWithCorp.directorId === director.id &&
      dirWithCorp.corporationId === data.corporationId
  );
  const officers =
    corporationHasDirector?.additionalDirectorInformation?.boardPosition?.join(
      ', '
    );

  const physicalAddress = director?.operatingAddress;

  const passport = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find((eachFile) => eachFile.documentType === LegacyDocumentType.Passport);

  const emiratesId = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find(
      (eachFile) => eachFile.documentType === LegacyDocumentType.EmiratesId
    );

  const visa = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find((eachFile) => eachFile.documentType === LegacyDocumentType.Visa);

  const { addressProof } = director;

  const openFile = async (fileId: number) => {
    const res = await getSignedUrlByFileId(fileId, null);
    window.open(res);
  };

  return (
    <div className="p-6 flex flex-col gap-2 bg-bg-disabled-subtle">
      <SummaryFieldKS 
        heading="First Name" 
        value={director?.directorFirstName || '-'} 
      />
      <SummaryFieldKS 
        heading="Last Name" 
        value={director?.directorLastName || '-'} 
      />
      <SummaryFieldKS heading="Email" value={director?.email} />
      <SummaryFieldKS heading="Phone Number" value={director?.number} />

      {director?.dateOfBirth ? (
        <SummaryFieldKS heading="Date of birth" value={director?.dateOfBirth} />
      ) : null}

      {Country.getCountryByCode(director?.personalDetails?.residence)?.name && (
        <SummaryFieldKS
          heading="Country of residence"
          value={
            Country.getCountryByCode(director?.personalDetails?.residence)?.name
          }
        />
      )}

      {director?.personalDetails?.nationality ? (
        <SummaryFieldKS
          heading="Nationality"
          value={
            Country.getCountryByCode(director?.personalDetails?.nationality)
              ?.name
          }
        />
      ) : null}

      {physicalAddress ? (
        <SummaryFieldKS
          heading="Current Address"
          value={[
            `${physicalAddress?.addressLine1}, ${physicalAddress?.addressLine2}`,
            `${physicalAddress?.city} - ${physicalAddress?.postalCode}`,
            `${
              State.getStateByCodeAndCountry(
                physicalAddress?.state,
                physicalAddress?.country
              )?.name
            }, ${Country.getCountryByCode(physicalAddress?.country)?.name}`,
          ]}
        />
      ) : null}
      {officers ? (
        <SummaryFieldKS heading="Roles" value={officers || 'No'} />
      ) : null}

      <SummaryFieldKS
        heading="Is Politically Exposed?"
        value={director?.personalDetails?.isPoliticallyExposed ? 'Yes' : 'No'}
      />

      <SummaryFieldKS
        heading="Have UAE Residence Visa?"
        value={director?.personalDetails?.haveUAEResidenceVisa ? 'Yes' : 'No'}
      />

      {director?.personalDetails?.passportCountryOfIssue ? (
        <SummaryFieldKS
          heading="Passport's Country of Issue"
          value={
            Country.getCountryByCode(
              director.personalDetails.passportCountryOfIssue
            )?.name
          }
        />
      ) : null}

      {director?.personalDetails?.passportNumber ? (
        <SummaryFieldKS
          heading="Passport Number"
          value={director?.personalDetails?.passportNumber}
        />
      ) : null}

      {director?.personalDetails?.passportExpirationDate ? (
        <SummaryFieldKS
          heading="Passport Expiration Date"
          value={NonTimezoneSensitiveDate.fromNativeDate(
            new Date(director.personalDetails.passportExpirationDate)
          ).format('Do MMM, YYYY')}
        />
      ) : null}

      {passport?.id ? (
        <DocumentSummaryField
          title="Passport"
          onClick={() => openFile(passport.id)}
          className="grid grid-cols-2 gap-6"
        />
      ) : null}

      {visa?.id ? (
        <DocumentSummaryField
          title="Residence Visa"
          onClick={() => openFile(visa.id)}
          className="grid grid-cols-2 gap-6"
        />
      ) : null}

      {emiratesId?.id ? (
        <DocumentSummaryField
          title="Emirates Id"
          onClick={() => openFile(emiratesId.id)}
          className="grid grid-cols-2 gap-6"
        />
      ) : null}

      {addressProof?.id ? (
        <DocumentSummaryField
          title="Address proof"
          onClick={() => openFile(addressProof.id)}
          className="grid grid-cols-2 gap-6"
        />
      ) : null}
    </div>
  );
};
